import { useState, useEffect, useRef } from 'react';
import { dataUrlToFile } from '../utils/imageUtils';
import { analyzeNose } from '../services/aestheticApi';

export function useInlineNoseAnalysis(beforeDataUrl, afterDataUrl, enabled) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!enabled || !beforeDataUrl || !afterDataUrl) {
      setResults(null);
      setError('');
      setLoading(false);
      return undefined;
    }

    const currentRequest = ++requestId.current;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      setResults(null);

      try {
        const beforeFile = await dataUrlToFile(beforeDataUrl, 'before.png');
        const afterFile = await dataUrlToFile(afterDataUrl, 'after.png');

        if (cancelled || currentRequest !== requestId.current) return;

        const formData = new FormData();
        formData.append('before_image', beforeFile);
        formData.append('after_image', afterFile);

        const data = await analyzeNose(formData);

        if (cancelled || currentRequest !== requestId.current) return;

        if (!data?.success) {
          throw new Error(data?.message || 'Nose analysis failed.');
        }

        setResults(data);
      } catch (err) {
        if (!cancelled && currentRequest === requestId.current) {
          const message = err.response?.data?.message || err.message || 'Unable to analyze nose images.';
          setError(message);
        }
      } finally {
        if (!cancelled && currentRequest === requestId.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [beforeDataUrl, afterDataUrl, enabled]);

  return { loading, error, results };
}
